import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ChevronRight } from "lucide-react";
import SEO from "../components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

function ServiceFaq({ faqs }) {
  return (
    <div className="mt-12" data-testid="service-faq">
      <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-3">FAQ</p>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
        Veelgestelde vragen
      </h2>
      <Accordion type="single" collapsible className="border border-slate-200 divide-y divide-slate-200">
        {faqs.map((faq, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-b-0 px-6">
            <AccordionTrigger
              data-testid={`faq-trigger-${i}`}
              className="text-left text-base font-semibold text-[#0F172A] hover:text-[#005F5F] hover:no-underline py-5"
            >
              {faq.q}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default function ServicePage({
  title,
  subtitle,
  heroImage,
  icon: Icon,
  description,
  features,
  extraContent,
  faqs,
  seoTitle,
  seoDescription,
  seoKeywords,
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="service-page">
      {seoTitle && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          breadcrumb={title}
          faqs={faqs}
        />
      )}
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={heroImage} alt={`${title} Breukelen - Cardoso Bouw en Onderhoud`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-16 w-full">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-white/60 text-sm mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#C5A059]">{title}</span>
          </nav>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase leading-tight">
            {subtitle || title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-8">
              {Icon && (
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#005F5F] mb-8">
                  <Icon className="w-6 h-6 text-[#C5A059]" />
                </div>
              )}
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
                  {description}
                </p>

                {features && features.length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
                      Wat wij bieden
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-3 p-4 bg-slate-50">
                          <div className="w-2 h-2 bg-[#C5A059] mt-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {extraContent}

                {faqs && faqs.length > 0 && <ServiceFaq faqs={faqs} />}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <div className="bg-[#005F5F] p-8 text-white">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-4">
                    Offerte aanvragen
                  </h3>
                  <p className="text-white/70 text-sm mb-6">
                    Gratis en vrijblijvend. Wij reageren binnen 24 uur.
                  </p>
                  <div className="space-y-3">
                    <Link
                      to="/contact"
                      data-testid="service-cta-offerte"
                      className="flex items-center justify-center w-full py-3 bg-[#C5A059] hover:bg-white hover:text-[#005F5F] text-white font-bold uppercase tracking-wider text-sm transition-all duration-300"
                    >
                      Offerte aanvragen
                    </Link>
                    <a
                      href="https://wa.me/31685071009"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="service-cta-whatsapp"
                      className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] text-white font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:bg-[#1da851]"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </a>
                    <a
                      href="tel:+31685071009"
                      data-testid="service-cta-phone"
                      className="flex items-center justify-center gap-2 w-full py-3 border-2 border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider text-sm transition-all duration-300"
                    >
                      <Phone className="w-4 h-4" />
                      06 85 07 10 09
                    </a>
                  </div>
                </div>

                <div className="bg-slate-50 p-8">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0F172A] mb-4">
                    Werkgebied
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Vanuit Breukelen werken wij in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. Wij komen graag bij u langs voor een vrijblijvende offerte.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-white uppercase mb-6">
            Interesse? Neem vandaag nog contact op
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="service-bottom-cta"
              className="bg-[#C5A059] hover:bg-white hover:text-[#005F5F] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300"
            >
              Offerte aanvragen
            </Link>
            <a
              href="tel:+31685071009"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#005F5F] font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Bellen
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
